import { computed } from 'vue';
import { useStates } from '../router/states';
import { PermissionFlag, PermissionHelper } from '../types/permissions';
import type { CompanyRoleModulePermission } from '../types/permissions';

export function useDynamicPermissions() {
  const { dynamicPermissionsState } = useStates();

  const userPermissions = computed<CompanyRoleModulePermission[]>(() => dynamicPermissionsState.permissions.value);
  
  const activeModules = computed<string[]>(() => dynamicPermissionsState.activeModules.value);
  
  const currentRole = computed(() => dynamicPermissionsState.currentRole.value);

  const hasPermission = (moduleCode: string, permission: PermissionFlag): boolean => {
    const modulePermission = userPermissions.value.find(
      (p) => p.moduleCode === moduleCode && p.active
    );
    
    if (!modulePermission) {
      return false;
    }
    
    return PermissionHelper.hasPermission(modulePermission.permissions, permission);
  };

  const hasModuleAccess = (moduleCode: string): boolean => {
    return activeModules.value.indexOf(moduleCode) !== -1;
  };

  const hasAllPermissions = (moduleCode: string, permissions: PermissionFlag[]): boolean => {
    return permissions.every((p) => hasPermission(moduleCode, p));
  };

  const hasAnyPermission = (moduleCode: string, permissions: PermissionFlag[]): boolean => {
    return permissions.some((p) => hasPermission(moduleCode, p));
  };

  const canRead = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.READ);
  };

  const canCreate = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.CREATE);
  };

  const canUpdate = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.UPDATE);
  };

  const canDelete = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.DELETE);
  };

  const canExport = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.EXPORT);
  };

  const canImport = (moduleCode: string): boolean => {
    return hasPermission(moduleCode, PermissionFlag.IMPORT);
  };

  const isAdmin = computed(() => {
    return currentRole.value?.code === 'ADMINISTRATOR';
  });

  const canManageUsers = computed(() => {
    return currentRole.value?.code === 'ADMINISTRATOR' || currentRole.value?.code === 'MANAGER';
  });

  const canManagePermissions = computed(() => {
    return currentRole.value?.code === 'ADMINISTRATOR';
  });

  const getModulePermission = (moduleCode: string): CompanyRoleModulePermission | undefined => {
    return userPermissions.value.find((p) => p.moduleCode === moduleCode && p.active);
  };

  const getModulePermissionValue = (moduleCode: string): number => {
    const permission = getModulePermission(moduleCode);
    return permission?.permissions || 0;
  };

  const canShowMenuItem = (permission: { moduleType: string; permissionFlag: PermissionFlag }) => {
    return computed(() => hasPermission(permission.moduleType.toUpperCase(), permission.permissionFlag));
  };

  return {
    userPermissions,
    activeModules,
    currentRole,
    hasPermission,
    hasModuleAccess,
    hasAllPermissions,
    hasAnyPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canExport,
    canImport,
    isAdmin,
    canManageUsers,
    canManagePermissions,
    getModulePermission,
    getModulePermissionValue,
    canShowMenuItem,
  };
}
